"use client";

import React from "react";
import { ArrowRight, Building2, House as Home, Briefcase, MapPin, ShieldCheck, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  badge: string;
  to: string;
  iconName: "commercial" | "roofing" | "projects" | "areas";
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  features: string;
}
const serviceItems: ServiceItem[] = [{
  id: "commercial",
  title: "Commercial Scaffolding",
  description: "Engineered access solutions, perimeter scaffolds, and temporary works designed for main contractors and commercial sites.",
  badge: "Specialist Access",
  to: "/CommercialScaffoldingPage",
  iconName: "commercial",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_site.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_site.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/commercial_scaffolding_site.webp-wvc-sizes",
  alt: "Commercial scaffolding erection on a multi-storey development",
  features: "TG20 compliant designs, method statements, handover certs"
}, {
  id: "roofing",
  title: "Roofing Scaffolding",
  description: "Dedicated edge protection, handrails, chimney scaffolds, and temporary roof covers ensuring secure access for roofers.",
  badge: "Safety Led",
  to: "/RoofingScaffoldingPage",
  iconName: "roofing",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffolding_protection.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffolding_protection.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffolding_protection.png-wvc-sizes",
  alt: "Roof edge protection and temporary scaffolding setup on a residential roof",
  features: "Chimney access, full edge guardrails, temporary roof spans"
}, {
  id: "projects",
  title: "Projects & Case Studies",
  description: "Review our recent scaffolding installations across commercial developments, heritage sites, and residential contracts.",
  badge: "Portfolio",
  to: "/ProjectsCaseStudiesPage",
  iconName: "projects",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_completed_projects.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_completed_projects.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_completed_projects.png-wvc-sizes",
  alt: "Completed scaffold structure on architectural site",
  features: "Detailed site photos, engineering specifications, project milestones"
}, {
  id: "areas",
  title: "Areas We Cover",
  description: "Serving Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the surrounding South East and Home Counties.",
  badge: "Regional Coverage",
  to: "/AreasWeCoverPage",
  iconName: "areas",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/south_east_scaffolding_coverage.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/south_east_scaffolding_coverage.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/south_east_scaffolding_coverage.png-wvc-sizes",
  alt: "Map representation of scaffold logistics in the South East",
  features: "Rapid mobilization across London, Surrey, and Home Counties"
}];
const sitePhotosQuery = new WP_Query({
  post_type: "site_photo",
  per_page: 50
});
function getResolvedImage(item: ServiceItem, posts: any[]): {
  src: string;
  srcset?: string;
  sizes?: string;
  alt: string;
} {
  if (item.id === "commercial") {
    const post = posts.find(p => p.title === "Temporary Roofs Related Commercial Image" || p.customFields?.photo_name === "Temporary Roofs Related Commercial Image");
    const resolvedSrc = post?.customFields?.image || post?.featuredImage;
    if (resolvedSrc) {
      return {
        src: resolvedSrc,
        srcset: "",
        sizes: "",
        alt: post?.customFields?.alt_text || post?.title || "Commercial scaffolding erection on a multi-storey development"
      };
    }
    return {
      src: item.image,
      srcset: item.srcset,
      sizes: item.sizes,
      alt: "Commercial scaffolding erection on a multi-storey development"
    };
  }
  if (item.id === "roofing") {
    const post = posts.find(p => p.title === "Temporary Roofs Related Roofing Image" || p.customFields?.photo_name === "Temporary Roofs Related Roofing Image");
    const resolvedSrc = post?.customFields?.image || post?.featuredImage;
    if (resolvedSrc) {
      return {
        src: resolvedSrc,
        srcset: "",
        sizes: "",
        alt: post?.customFields?.alt_text || post?.title || "Roof edge protection and temporary scaffolding setup on a residential roof"
      };
    }
    return {
      src: item.image,
      srcset: item.srcset,
      sizes: item.sizes,
      alt: "Roof edge protection and temporary scaffolding setup on a residential roof"
    };
  }
  if (item.id === "projects") {
    const post = posts.find(p => p.title === "Temporary Roofs Related Projects Image" || p.customFields?.photo_name === "Temporary Roofs Related Projects Image");
    const resolvedSrc = post?.customFields?.image || post?.featuredImage;
    if (resolvedSrc) {
      return {
        src: resolvedSrc,
        srcset: "",
        sizes: "",
        alt: post?.customFields?.alt_text || post?.title || "Completed scaffold structure on architectural site"
      };
    }
    return {
      src: item.image,
      srcset: item.srcset,
      sizes: item.sizes,
      alt: "Completed scaffold structure on architectural site"
    };
  }
  return {
    src: item.image,
    srcset: item.srcset,
    sizes: item.sizes,
    alt: item.alt
  };
}
function RelatedServicesConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: ServiceItem["iconName"]) => {
    switch (iconName) {
      case "commercial":
        return <Building2 className="size-5 text-primary" />;
      case "roofing":
        return <Home className="size-5 text-primary" />;
      case "projects":
        return <Briefcase className="size-5 text-primary" />;
      case "areas":
        return <MapPin className="size-5 text-primary" />;
    }
  };
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-10">
      {serviceItems.map((item, index) => {
      const isDynamicCard = item.id === "commercial" || item.id === "roofing" || item.id === "projects";
      const resolvedImage = getResolvedImage(item, posts);
      return <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.1
      }} transition={{
        duration: 0.45,
        delay: index * 0.08,
        ease: "easeOut"
      }} className="flex flex-col h-full">
            <Card className="h-full flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/40 group overflow-hidden">
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                {loading && isDynamicCard ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={resolvedImage.src} data-wvc-srcset={resolvedImage.srcset} data-wvc-sizes={resolvedImage.sizes} alt={resolvedImage.alt} loading="lazy" decoding="async" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />}
                <div className="absolute top-3 left-3">
                  <Badge variant="secondary" className="bg-secondary/90 text-secondary-foreground text-xs font-semibold backdrop-blur-xs">
                    {item.badge}
                  </Badge>
                </div>
              </div>

              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <CardHeader className="pt-5 pb-2">
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="p-2 rounded-md bg-accent text-accent-foreground">
                        {getIcon(item.iconName)}
                      </div>
                      <CardTitle className="text-lg font-bold tracking-tight text-card-foreground group-hover:text-primary transition-colors">
                        {item.title}
                      </CardTitle>
                    </div>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2 pb-4">
                    <div className="flex items-start gap-2 text-xs text-muted-foreground border-t border-border pt-3">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                      <span>{item.features}</span>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-0 pb-5">
                  <Button asChild variant="outline" size="sm" className="w-full justify-between group/btn hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors font-medium">
                    <Link to={item.to}>
                      <span>Explore {item.title.split(" ")[0]}</span>
                      <ArrowRight className="size-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
                    </Link>
                  </Button>
                </CardFooter>
              </div>
            </Card>
          </motion.div>;
    })}
    </div>;
}
export default function RelatedServices() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="relatedservicesgrid" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="text-primary border-border bg-card">
                <ShieldCheck className="size-3.5 text-primary" />
                BMF Scaffolding Ltd
              </Badge>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                NASC & CHAS Accredited
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Explore our services and coverage
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground">
              From full commercial access setups and roofing protection to completed project case studies and regional coverage across the South East.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild size="lg" className="shadow-sm font-semibold">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1.5" />
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Services Grid with PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "site_photo",
        per_page: 50,
        status: ["publish"]
      }}>
          <RelatedServicesConsumer />
        </PostsProvider>

        {/* Quick Consultation Banner */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        delay: 0.3,
        ease: "easeOut"
      }} className="mt-12 p-6 rounded-xl bg-accent text-accent-foreground border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-center sm:text-left">
            <div className="hidden sm:flex p-3 rounded-full bg-card text-primary shadow-xs">
              <Building2 className="size-5" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-sm sm:text-base">
                Need bespoke scaffolding calculations or rapid mobilization?
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Speak directly with our contracts team for reliable turnaround times and transparent pricing.
              </p>
            </div>
          </div>
          <Button asChild variant="secondary" size="sm" className="whitespace-nowrap shrink-0">
            <Link to="/ContactRequestQuotePage">
              Get an estimate
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}